import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Add, CalendarMonth, Refresh } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ListHeader from '../components/ListHeader';
import ListActionButton from '../components/ListActionButton';
import { RequirePermission, usePermissions } from '../contexts/PermissionContext';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import { listAppointmentCalendars, listAvailabilityRules } from '../services/appointments';
import type { AppointmentCalendar, AvailabilityRule } from '../types';
import { formatDateTime } from '../utils/date';

const MAX_APPOINTMENT_CALENDARS = 6;
const WEEKDAY_LABEL_KEYS = [
  'common:days.mon',
  'common:days.tue',
  'common:days.wed',
  'common:days.thu',
  'common:days.fri',
  'common:days.sat',
  'common:days.sun',
] as const;

/**
 * Format a stored `HH:mm[:ss]` schedule value according to the current UI locale.
 */
function formatWindowTime(value: string, locale: string): string {
  const [hourRaw, minuteRaw] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return value;
  }
  const date = new Date(2000, 0, 1, hour, minute, 0);
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
}

/**
 * Return a translated short weekday label while tolerating unexpected indexes.
 */
function weekdayLabel(day: number, t: TFunction): string {
  const labelKey = WEEKDAY_LABEL_KEYS[day];
  return labelKey ? t(labelKey) : String(day);
}

/**
 * Compact consecutive weekday numbers into localized labels such as `Seg-Sex`.
 */
function summarizeWeekdays(weekdays: number[], t: TFunction): string {
  const orderedDays = Array.from(new Set(weekdays)).sort((left, right) => left - right);
  const ranges: string[] = [];
  let rangeStart: number | null = null;
  let previousDay: number | null = null;

  const pushRange = () => {
    if (rangeStart === null || previousDay === null) {
      return;
    }
    const startLabel = weekdayLabel(rangeStart, t);
    const endLabel = weekdayLabel(previousDay, t);
    ranges.push(rangeStart === previousDay ? startLabel : `${startLabel}-${endLabel}`);
  };

  for (const day of orderedDays) {
    if (rangeStart === null) {
      rangeStart = day;
      previousDay = day;
      continue;
    }
    if (previousDay !== null && day === previousDay + 1) {
      previousDay = day;
      continue;
    }
    pushRange();
    rangeStart = day;
    previousDay = day;
  }
  pushRange();
  return ranges.join(', ');
}

/**
 * Build a compact weekly window summary from the active recurring rules.
 */
function buildWeeklyWindowSummary(rules: AvailabilityRule[] | null | undefined, t: TFunction, locale: string): string {
  if (rules === null) {
    return t('pages:appointments.card.weeklyWindowsUnavailable');
  }
  const activeRules = (rules ?? []).filter((rule) => rule.isActive);
  if (activeRules.length === 0) {
    return t('pages:appointments.card.noWeeklyWindows');
  }

  const groupedRules = new Map<string, { weekdays: number[]; startTime: string; endTime: string }>();
  for (const rule of activeRules) {
    const groupKey = `${rule.startTime}|${rule.endTime}`;
    const group = groupedRules.get(groupKey) ?? {
      weekdays: [],
      startTime: rule.startTime,
      endTime: rule.endTime,
    };
    group.weekdays.push(rule.weekday);
    groupedRules.set(groupKey, group);
  }

  return Array.from(groupedRules.values())
    .sort((left, right) => {
      const leftFirstDay = Math.min(...left.weekdays);
      const rightFirstDay = Math.min(...right.weekdays);
      if (leftFirstDay !== rightFirstDay) {
        return leftFirstDay - rightFirstDay;
      }
      return left.startTime.localeCompare(right.startTime);
    })
    .map((group) => t('pages:appointments.card.weeklyWindowSummary', {
      days: summarizeWeekdays(group.weekdays, t),
      start: formatWindowTime(group.startTime, locale),
      end: formatWindowTime(group.endTime, locale),
    }))
    .join(' · ');
}

export default function AppointmentCalendarListPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { hasPermission } = usePermissions();
  const { formatNumber, locale } = useNumberFormat();
  usePageTitle(t('pages:appointments.title'));

  const [rows, setRows] = useState<AppointmentCalendar[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rulesByCalendarId, setRulesByCalendarId] = useState<Record<string, AvailabilityRule[] | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listAppointmentCalendars({
        page: 1,
        pageSize: MAX_APPOINTMENT_CALENDARS,
        ordering: 'name',
      });
      const listRows = Array.isArray(payload.results) ? payload.results : [];
      const ruleEntries = await Promise.all(listRows.map(async (calendar) => {
        try {
          const rules = await listAvailabilityRules(calendar.id);
          return [calendar.id, rules] as const;
        } catch (rulesError: unknown) {
          console.error('Error loading appointment calendar rules:', rulesError);
          return [calendar.id, null] as const;
        }
      }));
      setRows(listRows);
      setTotalRows(Number(payload.count ?? listRows.length ?? 0));
      setRulesByCalendarId(Object.fromEntries(ruleEntries) as Record<string, AvailabilityRule[] | null>);
    } catch (requestError: unknown) {
      console.error('Error loading appointment calendars:', requestError);
      setRows([]);
      setTotalRows(0);
      setRulesByCalendarId({});
      setError(t('pages:appointments.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  if (!hasPermission('appointments_calendars_read')) {
    return <Alert severity="warning">{t('pages:appointments.accessDenied')}</Alert>;
  }

  const hasReachedCalendarLimit = totalRows >= MAX_APPOINTMENT_CALENDARS;
  const headerActions = (
    <>
      <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={loadRows}>
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="appointments_calendars_manage">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/appointments/new"
          disabled={loading || hasReachedCalendarLimit}
        >
          {t('pages:appointments.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  return (
    <Box>
      <ListHeader
        icon={<CalendarMonth />}
        title={t('pages:appointments.title')}
        subtitle={t('pages:appointments.subtitle')}
        actions={headerActions}
      />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('pages:appointments.limitUsage', {
            created: formatNumber(totalRows),
            limit: formatNumber(MAX_APPOINTMENT_CALENDARS),
          })}
        </Typography>
        {hasReachedCalendarLimit ? (
          <Alert severity="info">
            {t('pages:appointments.limitReached', { limit: formatNumber(MAX_APPOINTMENT_CALENDARS) })}
          </Alert>
        ) : null}
        {loading ? (
          <Typography color="text.secondary">{t('common:common.loading')}</Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t('pages:appointments.empty')}</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {rows.map((calendar) => {
              const fixedSlotDuration = calendar.minSlotDurationMinutes === calendar.maxSlotDurationMinutes
                && calendar.defaultSlotDurationMinutes === calendar.minSlotDurationMinutes;
              const noticeLabel = calendar.minNoticeMinutes > 0
                ? t('pages:appointments.card.minNotice', { notice: formatNumber(calendar.minNoticeMinutes) })
                : t('pages:appointments.card.noMinNotice');
              const slotPolicyLabel = fixedSlotDuration
                ? t('pages:appointments.card.fixedSlotPolicy', {
                  duration: formatNumber(calendar.defaultSlotDurationMinutes),
                  step: formatNumber(calendar.slotStepMinutes),
                  notice: noticeLabel,
                  daysAhead: formatNumber(calendar.maxDaysAhead),
                })
                : t('pages:appointments.card.flexibleSlotPolicy', {
                  defaultValue: formatNumber(calendar.defaultSlotDurationMinutes),
                  min: formatNumber(calendar.minSlotDurationMinutes),
                  max: formatNumber(calendar.maxSlotDurationMinutes),
                  step: formatNumber(calendar.slotStepMinutes),
                  notice: noticeLabel,
                  daysAhead: formatNumber(calendar.maxDaysAhead),
                });

              return (
                <Card key={calendar.id} variant="outlined" sx={{ height: '100%' }}>
                  <CardActionArea
                    component={RouterLink}
                    to={`/appointments/${calendar.id}`}
                    sx={{ height: '100%', alignItems: 'stretch' }}
                  >
                    <CardContent
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" noWrap title={calendar.name}>
                            {calendar.name}
                          </Typography>
                          {calendar.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {calendar.description}
                            </Typography>
                          ) : null}
                        </Box>
                        <Chip
                          size="small"
                          color={calendar.isActive ? 'success' : 'default'}
                          label={calendar.isActive ? t('common:status.active') : t('common:status.inactive')}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip size="small" variant="outlined" label={calendar.timezone} />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('pages:appointments.card.capacity', {
                            value: formatNumber(calendar.defaultCapacityPerSlot),
                          })}
                        />
                      </Box>

                      <Stack spacing={0.75}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {t('pages:appointments.card.weeklyWindows')}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {buildWeeklyWindowSummary(rulesByCalendarId[calendar.id], t, locale)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {t('pages:appointments.card.slotPolicyTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {slotPolicyLabel}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                        {t('pages:appointments.card.updatedAt', {
                          value: formatDateTime(calendar.updatedAt, { locale, fallback: '-' }),
                        })}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
